import type { JellyAsset } from "../engine/content/types";
import Ajv from "ajv";
import { contentManifest } from "../generated/content-manifest";
import type {
  DropDefinition,
  LevelDefinition,
  SlimeDefinition,
  WorldDefinition,
} from "../generated/content-types";
import dropSchema from "../schemas/drop.schema.json";
import levelSchema from "../schemas/level.schema.json";
import jellyVisualSchema from "../schemas/jelly-visual.schema.json";
import slimeSchema from "../schemas/slime.schema.json";
import worldSchema from "../schemas/world.schema.json";
import type { TestLevelFixture } from "../engine/core/types";
import {
  type CompiledDropDefinition,
  type CompiledLevel,
  type CompiledRule,
  type CompiledSlimeDefinition,
  type CompiledWorld,
  type ContentCatalog,
  type RulePhase,
  type RuleRegistry,
} from "../engine/content/types";

const phases: readonly RulePhase[] = [
  "levelStart",
  "beforePhysics",
  "afterPhysics",
  "afterResolution",
  "afterFlush",
  "levelEnd",
];
// 压力爆浆的能力位由 T045 起加入；旧循环的能力位随决策 0016 移除，当前两侧都没有能力位。
const behaviorFlag = new Map<string, number>();
const dropBehaviorFlag = new Map<string, number>();

/** 磁盘内容的可变副本。调参 overlay 叠加在它之上后重新编译，见[决策 0017]。 */
export interface RawContent {
  visuals: Record<string, JellyAsset>;
  slimes: SlimeDefinition[];
  drops: DropDefinition[];
  worlds: WorldDefinition[];
  levels: LevelDefinition[];
}

export function rawContentFromManifest(): RawContent {
  return structuredClone(contentManifest) as unknown as RawContent;
}

export function compileCatalog(
  ruleRegistry: RuleRegistry,
  raw: RawContent = rawContentFromManifest(),
): ContentCatalog {
  const rawContentVisuals = raw.visuals;
  const rawSlimes = raw.slimes;
  const rawDrops = raw.drops;
  const rawWorlds = raw.worlds;
  const rawLevels = raw.levels;
  validateRuntime(rawSlimes, rawDrops, rawWorlds, rawLevels, rawContentVisuals);

  const slimeIds = uniqueIdMap(rawSlimes, "slime");
  const dropIds = uniqueIdMap(rawDrops, "drop");
  const worldIds = uniqueIdMap(rawWorlds, "world");
  const drops: CompiledDropDefinition[] = rawDrops.map((raw) => {
    let behaviors = 0;
    for (const behavior of raw.behaviors)
      behaviors |= dropBehaviorFlag.get(behavior.type) ?? 0;
    return {
      id: raw.id,
      displayName: raw.displayName,
      radius: raw.physics.radius,
      speed: raw.physics.speed,
      behaviors,
      visualColor: raw.visual.color,
      pulse: raw.visual.pulse,
    };
  });
  const slimes: CompiledSlimeDefinition[] = rawSlimes.map((raw) => {
    const jelly = rawContentVisuals[raw.id];
    let behaviors = 0;
    for (const behavior of raw.behaviors)
      behaviors |= behaviorFlag.get(behavior.type) ?? 0;
    return {
      id: raw.id,
      displayName: raw.displayName,
      radiusRest: raw.physics.radiusRest,
      radiusFull: raw.physics.radiusFull,
      burstVolume: raw.physics.burstVolume,
      spring: raw.physics.squash.spring,
      damping: raw.physics.squash.damping,
      mass: raw.physics.mass,
      fallScale: raw.physics.fallScale,
      absorb: raw.hooks.absorb,
      splashFraction: raw.hooks.splashFraction,
      splashRadius: raw.hooks.splashRadius,
      ringWeight: raw.hooks.ringWeight,
      stickiness: raw.hooks.stickiness,
      behaviors,
      jelly,
      visualColor: jelly.parameters.color,
    };
  });
  const worlds: CompiledWorld[] = rawWorlds.map((raw) => ({
    id: raw.id,
    width: raw.size.width,
    height: raw.size.height,
    gravity: raw.physics.gravity,
    fallDamping: raw.physics.fallDamping,
    horizontalDrag: raw.physics.horizontalDrag,
    drop: requiredId(dropIds, raw.player.drop, "drop"),
    fireInterval: raw.player.fireInterval,
    sprayJitter: raw.player.sprayJitter,
    waterRate: raw.pressure.waterRate,
    waterImpulse: raw.pressure.waterImpulse,
    liftFactor: raw.pressure.liftFactor,
    baseFraction: raw.pressure.baseFraction,
    leakRate: raw.pressure.leakRate,
    compression: raw.pressure.compression,
    repulsion: raw.pressure.repulsion,
    wallContactDepth: raw.pressure.wallContactDepth,
    ampCoef: raw.burst.ampCoef,
    ampCap: raw.burst.ampCap,
    ringWindow: raw.burst.ringWindow,
    splashFraction: raw.burst.splashFraction,
    splashRadius: raw.burst.splashRadius,
    splashPush: raw.burst.splashPush,
    bondLife: raw.bonds.life,
    bondStrength: raw.bonds.strength,
    bondSeal: raw.bonds.seal,
    bondCooldown: raw.bonds.cooldown,
    leakLine: raw.ground.leakLine,
    playerRow: raw.ground.playerRow,
    rules: compileRules(raw.rules, ruleRegistry),
  }));

  const compileLevel = (
    raw: LevelDefinition,
    testFixture = false,
  ): CompiledLevel => {
    const world = worlds[requiredId(worldIds, raw.world, "world")];
    const merged = [...world.rules, ...compileRules(raw.rules, ruleRegistry)];
    const byPhase = Object.fromEntries(
      phases.map((phase) => [
        phase,
        merged.filter((rule) => rule.phase === phase),
      ]),
    ) as unknown as Record<RulePhase, readonly CompiledRule[]>;
    return {
      id: raw.id,
      order: raw.order,
      name: raw.metadata.name,
      kicker: raw.metadata.kicker,
      objective: raw.metadata.objective,
      world,
      entities: raw.entities.map((entity) => ({
        instanceId: entity.id,
        type: requiredId(slimeIds, entity.definition, "slime"),
        x: entity.position.x,
        y: entity.position.y,
      })),
      rules: byPhase,
      testFixture,
    };
  };
  const levels = rawLevels
    .map((level) => compileLevel(level))
    .sort((a, b) => a.order - b.order);

  return {
    slimes,
    drops,
    levels,
    slimeIds,
    dropIds,
    compileFixture(fixture: TestLevelFixture): CompiledLevel {
      const clearRule =
        fixture.clearWhenEmpty === false
          ? []
          : [
              {
                instanceId: "fixture-clear",
                type: "core:clear-all",
                params: {},
              },
            ];
      const raw: LevelDefinition = {
        $schema: "test-fixture",
        schemaVersion: 1,
        id: fixture.id,
        order: 1,
        world: worlds[0].id,
        metadata: {
          name: fixture.name,
          kicker: fixture.kicker ?? "TEST FIXTURE",
          objective: fixture.objective ?? "Deterministic fixture",
        },
        rules: [...clearRule, ...(fixture.rules ?? [])],
        entities: fixture.entities.map((entity) => ({
          id: entity.id,
          kind: "slime" as const,
          definition: entity.definition,
          position: { x: entity.x, y: entity.y },
        })),
        editor: {
          camera: { x: 240, y: 320, zoom: 1 },
          gridSize: 4,
          snapEnabled: true,
        },
      };
      return compileLevel(raw, true);
    },
  };
}

function compileRules(
  rawRules: readonly {
    instanceId: string;
    type: string;
    params: Record<string, unknown>;
  }[],
  registry: RuleRegistry,
): CompiledRule[] {
  return rawRules.map((raw) => {
    const registration = registry.get(raw.type);
    if (registration === undefined)
      throw new Error(`Unknown rule type: ${raw.type}`);
    return { ...raw, phase: registration.phase, execute: registration.execute };
  });
}

function uniqueIdMap(
  values: readonly { id: string }[],
  kind: string,
): Map<string, number> {
  const map = new Map<string, number>();
  values.forEach((value, index) => {
    if (map.has(value.id)) throw new Error(`Duplicate ${kind} id: ${value.id}`);
    map.set(value.id, index);
  });
  return map;
}

function requiredId(
  map: ReadonlyMap<string, number>,
  id: string,
  kind: string,
): number {
  const value = map.get(id);
  if (value === undefined) throw new Error(`Unknown ${kind} id: ${id}`);
  return value;
}

function validateRuntime(
  slimes: SlimeDefinition[],
  drops: DropDefinition[],
  worlds: WorldDefinition[],
  levels: LevelDefinition[],
  visuals: Record<string, JellyAsset>,
): void {
  const ajv = new Ajv({ allErrors: true, strict: false });
  const validateVisual = ajv.compile(jellyVisualSchema);
  for (const slime of slimes) {
    const asset = visuals[slime.id];
    if (!asset) throw new Error(`Missing visual definition: ${slime.id}`);
    if (typeof asset.source !== "string")
      throw new Error(`Invalid body source: ${slime.id}`);
    if (!validateVisual(asset.parameters))
      throw new Error(
        `Invalid visual definition ${slime.id}: ${ajv.errorsText(validateVisual.errors)}`,
      );
  }
  const groups = [
    [slimeSchema, slimes],
    [dropSchema, drops],
    [worldSchema, worlds],
    [levelSchema, levels],
  ] as const;
  for (const [schema, values] of groups) {
    const validate = ajv.compile(schema);
    for (const value of values)
      if (!validate(value))
        throw new Error(
          `Invalid content ${value.id}: ${ajv.errorsText(validate.errors)}`,
        );
  }
}
